/**
 * TA v4.3 — Intro screen (Pre-assessment).
 *
 * Renders the candidate welcome + 3-phase overview + rules of engagement.
 * Server-side clock starts ONLY when user clicks "Begin Phase 1".
 *
 * Acceptance criteria (TCA QA Protocol v2.0):
 *  - 45-min total displayed
 *  - 3 phases with durations 12/23/10 min
 *  - Explicit consent text on auto-save + telemetry capture
 *  - No back-button after begin
 */
import React, { useMemo } from "react";
import { Loader2, AlertTriangle, Clock, Eye, Lock, ArrowRight } from "lucide-react";

const SECTION_NAMES = {
    ta_phase1: "Rapid Fire",
    ta_phase2: "Applied Warfare",
    ta_phase3: "Judgment Under Fire",
};

const PHASE_DESCRIPTIONS = {
    ta_phase1: "Short, targeted questions that test your fluency with the tools and concepts in your day-to-day work. Some questions contain deliberate errors — flagging them is part of the test.",
    ta_phase2: "Multi-part problems where each response builds on the last. You'll debug, design, and reconstruct from contradictory evidence. Part B will be locked until you commit to Part A.",
    ta_phase3: "Open-ended judgment calls with real trade-offs. You'll write the actual communication you'd send to a stakeholder. The last question asks you to grade yourself.",
};

export default function TAIntroScreen({ data, onBegin, beginning, error }) {
    const sections = useMemo(() => (data?.template?.content?.content || []).filter(s => s.type === "section"), [data]);
    const meta = data?.template?.content?.metadata || {};
    const totalSeconds = Number(meta.total_time_seconds) || sections.reduce((a, s) => a + ((s.timer || {}).duration_seconds || 0), 0);
    const totalMinutes = Math.round(totalSeconds / 60);
    const candidateFirstName = (data?.candidate?.name || "").split(" ")[0] || "there";

    return (
        <div className="min-h-screen bg-background" data-testid="ta-intro-screen">
            <header className="border-b border-border/40 bg-card">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-sm font-display font-bold text-white tracking-wide">THCO · Technical Assessment</h1>
                        <p className="text-[11px] text-gray-600 font-mono">{data?.template?.job_title} · {data?.candidate?.email}</p>
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono">v{meta.version || "4.3"}</div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500 font-mono mb-3">Pre-assessment briefing</p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-white mb-3 leading-tight">
                    Welcome, {candidateFirstName}.
                </h2>
                <p className="text-sm text-gray-400 leading-relaxed mb-8 max-w-xl">
                    This is a {totalMinutes}-minute technical assessment for <span className="text-white font-semibold">{data?.template?.job_title}</span>.
                    It's designed to capture how you actually think under pressure — not whether you can paste a question into an AI tool and read the answer back. Take it where you can focus.
                </p>

                {/* Phase strip */}
                <div className="space-y-3 mb-10">
                    {sections.map((s) => {
                        const mins = Math.round(((s.timer || {}).duration_seconds || 0) / 60);
                        return (
                            <div key={s.id} className="flex items-start gap-4 border border-border/40 bg-card px-5 py-4" data-testid={`ta-intro-section-${s.id}`}>
                                <div className="text-xs font-mono text-gray-500 w-14 shrink-0 pt-1">{`P${s.section_number}`}</div>
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <h3 className="text-sm font-display font-bold text-white">{SECTION_NAMES[s.id] || s.title}</h3>
                                        <span className="text-[11px] text-gray-500 font-mono flex items-center gap-1"><Clock className="w-3 h-3" />{mins} min</span>
                                    </div>
                                    <p className="text-xs text-gray-500 leading-relaxed">{PHASE_DESCRIPTIONS[s.id]}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Rules of engagement */}
                <div className="bg-card border border-border/40 p-5 mb-10">
                    <h4 className="text-xs uppercase tracking-wider font-mono text-gray-400 mb-3">Before you begin</h4>
                    <ul className="space-y-2.5 text-xs text-gray-400 leading-relaxed">
                        <li className="flex gap-3"><Lock className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" /><span>Once a phase ends or you submit it, it cannot be reopened. The clock is enforced server-side — pausing this tab does not pause it.</span></li>
                        <li className="flex gap-3"><Eye className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" /><span>We record the cadence of your typing, when you paste, and when you switch tabs. We do not record what's on your screen. This data informs scoring, not surveillance.</span></li>
                        <li className="flex gap-3"><AlertTriangle className="w-3.5 h-3.5 text-amber-300/80 shrink-0 mt-0.5" /><span>Some questions contain deliberate errors. Calling them out earns credit. Answering them as if correct doesn't.</span></li>
                        <li className="flex gap-3"><ArrowRight className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" /><span>You can type or paste your answers. Pasting is permitted and tracked — we'll look at the pattern, not the act.</span></li>
                    </ul>
                </div>

                {error && (
                    <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 mb-6" data-testid="ta-intro-error">
                        <p className="text-xs text-red-300">{error}</p>
                    </div>
                )}

                <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-600 font-mono">By beginning, you confirm you are <span className="text-gray-400">{data?.candidate?.name}</span> and that you are completing this on your own.</p>
                    <button
                        onClick={onBegin}
                        disabled={beginning}
                        className="px-6 py-3 bg-white text-black text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2 transition-colors"
                        data-testid="ta-begin-btn"
                    >
                        {beginning ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <>Begin Phase 1 <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </div>
            </main>
        </div>
    );
}
